import React, { Component } from 'react';
import { PriceType, RoMaxPrice } from '~/Api/User/ProductGroup/RoMaxPrice';
import { formatCountriesError } from '~/UI';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading';

import styles from './styles.less';

interface Props {
	roMaxPrice: RoMaxPrice;
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	public render() {
		const {
			roMaxPrice: { finalCalculation },
		} = this.props;

		return (
			<div className={styles.container}>
				<div className={styles.columns}>
					<div className={styles.leftColumn}>
						<Heading level={3}>Applicable reference prices:</Heading>
						{finalCalculation.publicCatalogPrice?.fromProducts.map((product, i) => (
							<div
								className={styles.row}
								data-product-id={product.id}
								data-testid={`lowest-exfactory-price-${i}`}
								key={product.id}
							>
								<span className={styles.number}>{i + 1}.</span>

								<span className={styles.countryIcon}>
									<CountryIcon code={product.dataSource.country.alpha2} name={product.dataSource.country.name} />
								</span>

								<span className={styles.countryDescription}>
									{product.dataSource.country.alpha2}
									<br />
									{product.dataSource.shortName}
								</span>
							</div>
						))}
					</div>
					<div className={styles.centerColumn}>
						<Heading level={3}>Calculated max price:</Heading>

						<div className={styles.row}>
							<p className={styles.rowName}>
								Price for CANAMED
								{finalCalculation.canamedPrice !== null
									? ` (${this.renderPriceType(finalCalculation.canamedPrice.priceType)})`
									: null}
								:
							</p>
							<p className={styles.rowValue}>
								{finalCalculation.canamedPrice !== null ? (
									<strong>{finalCalculation.canamedPrice.price.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.rowName}>
								Price for Public Catalog
								{finalCalculation.publicCatalogPrice !== null
									? ` (${this.renderPriceType(finalCalculation.publicCatalogPrice.priceType)})`
									: null}
								:
							</p>
							<p className={styles.rowValue}>
								{finalCalculation.publicCatalogPrice !== null ? (
									<strong>{finalCalculation.publicCatalogPrice.price.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.rowName}>Generic reference price (65%):</p>
							<p className={styles.rowValue}>
								{finalCalculation.genericReferencePrice !== null ? (
									<strong>{finalCalculation.genericReferencePrice.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.rowName}>Biosimilars reference price (80%):</p>
							<p className={styles.rowValue}>
								{finalCalculation.biosimilarReferencePrice !== null ? (
									<strong>{finalCalculation.biosimilarReferencePrice.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>

						{finalCalculation.emptyResultReason.length > 0 ? (
							<div className={styles.row}>
								<p>{this.renderEmptyResultReason()}</p>
							</div>
						) : null}
					</div>
					<div className={styles.rightColumn}>
						<Heading level={3}>&nbsp;</Heading>

						<div className={styles.row}>
							<p className={styles.smallRowName}>
								Current ex-factory price (
								{finalCalculation.referencedProduct.canamed?.dataSource.shortName ?? 'CANAMED'}
								):
							</p>
							<p className={styles.smallRowValue}>
								<strong>
									{finalCalculation.referencedProduct.canamed?.currentExFactoryPrice.roundedHumanFormat()}
								</strong>
							</p>
						</div>

						<div className={styles.row}>
							<p className={styles.smallRowName}>
								Current ex-factory price (
								{finalCalculation.referencedProduct.publicCatalog?.dataSource.shortName ?? 'Public Catalog'}
								):
							</p>
							<p className={styles.smallRowValue}>
								<strong>
									{finalCalculation.referencedProduct.publicCatalog?.currentExFactoryPrice.roundedHumanFormat()}
								</strong>
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	private renderEmptyResultReason() {
		const {
			roMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.findMaxOneReferenceProductError();

		if (finalCalculation.hasMissingProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.requiresMinOneReferenceProductErrorCode()) {
			return <strong className={styles.rowError}>Calculation requires at least one reference country product</strong>;
		}
		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each reference country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.isMissingReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.isIllegalProductPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		if (finalCalculation.requiresMaxOneProductPerDataSource()) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one Romanian product for each data source
				</strong>
			);
		}
		return null;
	}

	private renderPriceType(priceType: PriceType) {
		switch (priceType) {
			case PriceType.essentialMax:
				return 'approved price in Canamed + 20%';
			case PriceType.fxUpdatePrice:
				return 'FX Update';
			case PriceType.prgPrbPrice:
				return 'PRG/PRB';
			case PriceType.priPrice:
				return 'PRI';
			case PriceType.listAvgOf3Min:
				return 'average of 3 lowest ex-factory prices';
			case PriceType.listMin:
				return 'lowest ex-factory price';
			default:
				return '';
		}
	}
}
